import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitBanner, KitField, KitFormBar, KitKV, KitTextarea, KitTimeline } from '../common/MobileKitExtras';
import { useAppBarAction, useAppBarTitle } from '../layout/AppBarActions';
import { getSaleResumeFinalAmount, hasNonZeroProductItemDiscount } from '../../common/saleUtils';

/**
 * Detalhe da venda no shell mobile — `prototipos-mobile/vendas/detalhe.html`.
 * Só apresentação: dados, permissões e ações (imprimir / cancelar / estornar) vêm de `pages/sales/View.js`.
 */

const PackageIcon = protoIcon('package');
const PrintIcon = protoIcon('print');
const ShareIcon = protoIcon('share');
const BanIcon = protoIcon('ban');
const PlayIcon = protoIcon('play');

const STATUS_VIEW = {
    iniciada: { label: 'Iniciada', tone: 'info' },
    finalizada: { label: 'Concluída', tone: 'success' },
    cancelada: { label: 'Cancelada', tone: 'danger' },
    estornada: { label: 'Estornada', tone: 'warning' },
};

const statusView = (status) => STATUS_VIEW[String(status || '').trim().toLowerCase()] || { label: status || '—', tone: undefined };

const PAYMENT_LABEL = {
    'CRÉDITO': 'Crédito',
    'DÉBITO': 'Débito',
    PIXKEY: 'Pix',
    PIXQRCODE: 'Pix QR Code',
    DINHEIRO: 'Dinheiro',
};

const isZeroMoney = (value) => {
    if (value === undefined || value === null || value === '') return true;
    const n = parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
    return Number.isNaN(n) || n === 0;
};

function initials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'CF';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

// `.avatar.lg` (+ `.purple`)
function Avatar({ text, purple }) {
    return (
        <Box
            component="span"
            sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundImage: purple ? 'none' : theme.palette.gradient.brand,
                backgroundColor: purple ? theme.palette.secondary.bg : undefined,
                color: purple ? theme.palette.secondary.main : '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 20,
                flexShrink: 0,
            })}
        >
            {text}
        </Box>
    );
}

function paymentRow(payment) {
    const base = PAYMENT_LABEL[payment.method] || payment.method;
    const installments = Number(payment.number_installment) || 1;
    if (payment.method === 'CRÉDITO' && installments > 1) {
        return { label: `${base} ${installments}x`, value: `R$ ${payment.installment} × ${installments}` };
    }
    return { label: base, value: `R$ ${payment.amount}` };
}

export default function MobileSaleDetail({
    sale,
    items,
    payments,
    loading,
    canCancel,
    canReverse,
    canContinue,
    loadingAction,
    onPrint,
    onContinue,
    cancelOpen,
    onOpenCancel,
    onCloseCancel,
    cancelReason,
    onCancelReasonChange,
    onConfirmCancel,
    reverseOpen,
    onOpenReverse,
    onCloseReverse,
    onConfirmReverse,
}) {
    const [actionsOpen, setActionsOpen] = useState(false);
    const view = statusView(sale.status);

    useAppBarTitle(!loading && sale.number ? `Venda #${sale.number}` : '');
    useAppBarAction({ label: 'Ações', icon: ShareIcon, onClick: () => setActionsOpen(true), disabled: loading });

    const customerName = sale.customer_name || '';
    const gross = sale.items_gross_subtotal !== undefined && sale.items_gross_subtotal !== '' ? sale.items_gross_subtotal : sale.total_amount;
    const hasItemDiscount = hasNonZeroProductItemDiscount(sale);
    const hasSaleDiscount = !isZeroMoney(sale.total_discount_applied);
    const hasTax = !isZeroMoney(sale.total_tax_machine);

    // Histórico derivado do status e da data da venda (a API não expõe eventos).
    const shortDate = sale.date ? `${String(sale.date).slice(0, 5)} · ${String(sale.date).slice(11, 16)}` : '';
    const timeline = [];
    const statusKey = String(sale.status || '').trim().toLowerCase();
    if (statusKey === 'iniciada') {
        timeline.push({ title: 'Venda iniciada', sub: shortDate });
    } else if (statusKey === 'cancelada') {
        timeline.push({ title: 'Venda cancelada', sub: shortDate, state: 'done' });
    } else {
        timeline.push({ title: 'Venda concluída', sub: shortDate, state: 'done' });
        if (statusKey === 'finalizada') timeline.push({ title: 'Estoque baixado', sub: shortDate, state: 'done' });
        if (statusKey === 'estornada') timeline.push({ title: 'Venda estornada', sub: shortDate, state: 'done' });
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Skeleton variant="rounded" height={170} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={190} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <Avatar text={customerName ? initials(customerName) : 'CF'} purple={Boolean(customerName)} />
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>{customerName || 'Consumidor final'}</Box>
                        </Box>
                    </Box>
                    <KitPill tone={view.tone}>{view.label}</KitPill>
                </Box>
                <Box sx={{ height: '1px', backgroundColor: 'divider', m: '14px 0' }} />
                <KitKV label="Data" value={sale.date ? String(sale.date).replace(' ', ' · ') : '—'} />
                <KitKV label="Vendedor" value={sale.user || '—'} />
                <KitKV label="Caixa" value={sale.cash_register_name || '—'} />
            </KitCard>

            {statusKey === 'cancelada' && sale.reason_cancel && (
                <Box sx={{ mt: '12px' }}>
                    <KitBanner tone="danger" title="Motivo do cancelamento">{sale.reason_cancel}</KitBanner>
                </Box>
            )}

            <SecH title={`Itens (${items.length})`} />
            <KitList>
                {items.length === 0 && (
                    <KitItem icon={PackageIcon} title="Nenhum produto nesta venda" />
                )}
                {items.map((item, index) => (
                    <KitItem
                        key={`${item.product_id || item.name}-${index}`}
                        icon={PackageIcon}
                        title={item.name}
                        sub={`${item.quantity} × R$ ${item.price}${item.item_discount_amount ? ` · − R$ ${item.item_discount_amount}` : ''}`}
                        end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {item.total}</Box>}
                    />
                ))}
            </KitList>

            <SecH title="Pagamento" />
            <KitCard>
                {payments.length === 0 && (
                    <KitKV label="Nenhum pagamento registrado para esta venda." value="" />
                )}
                {payments.map((payment, index) => {
                    const row = paymentRow(payment);
                    return <KitKV key={index} label={row.label} value={row.value} />;
                })}
                <Box>
                    <KitKV totals label="Subtotal" value={`R$ ${gross}`} />
                    {hasItemDiscount && (
                        <KitKV totals label="Desconto nos produtos" value={`− R$ ${sale.total_item_discount}`} />
                    )}
                    {hasSaleDiscount && (
                        <KitKV totals label="Desconto na venda" value={`− R$ ${sale.total_discount_applied}`} />
                    )}
                    {hasTax && (
                        <KitKV totals label={`Taxa (${sale.total_percent_tax || '0'}%)`} value={`R$ ${sale.total_tax_machine}`} />
                    )}
                    <KitKV totals big label="Total" value={`R$ ${getSaleResumeFinalAmount(sale)}`} />
                </Box>
            </KitCard>

            <SecH title="Histórico" />
            <KitCard>
                <KitTimeline items={timeline} />
            </KitCard>

            <KitFormBar>
                <KitButton onClick={onPrint}><PrintIcon size={18} /> Imprimir</KitButton>
                {canContinue && (
                    <KitButton variant="primary" onClick={onContinue}><PlayIcon size={18} /> Continuar</KitButton>
                )}
                {canReverse && (
                    <KitButton variant="danger" onClick={onOpenReverse}>Estornar</KitButton>
                )}
            </KitFormBar>

            <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Ações" actionList>
                <Box>
                    <KitItem
                        icon={PrintIcon}
                        title="Imprimir cupom"
                        chevron
                        sx={{ border: 0, borderBottom: 0, p: '12px 4px' }}
                        onClick={() => {
                            setActionsOpen(false);
                            onPrint();
                        }}
                    />
                    {canCancel && (
                        <KitItem
                            icon={BanIcon}
                            tone="red"
                            title="Cancelar venda"
                            sub="Exige motivo"
                            chevron
                            sx={{ border: 0, borderBottom: 0, p: '12px 4px' }}
                            onClick={() => {
                                setActionsOpen(false);
                                onOpenCancel();
                            }}
                        />
                    )}
                </Box>
            </MobileSheet>

            <MobileSheet
                open={reverseOpen}
                onClose={onCloseReverse}
                title="Estornar venda"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={onCloseReverse} disabled={loadingAction}>Voltar</KitButton>
                        <KitButton
                            sx={{ flex: 1, backgroundColor: 'error.main', color: '#fff', border: 0 }}
                            onClick={onConfirmReverse}
                            disabled={loadingAction}
                        >
                            Confirmar estorno
                        </KitButton>
                    </>
                )}
            >
                <Box sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.4 }}>
                    Deseja realmente estornar esta venda? Os pagamentos registrados permanecem para conferência.
                </Box>
            </MobileSheet>

            <MobileSheet
                open={cancelOpen}
                onClose={onCloseCancel}
                title="Cancelar venda"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={onCloseCancel} disabled={loadingAction}>Voltar</KitButton>
                        <KitButton
                            sx={{ flex: 1, backgroundColor: 'error.main', color: '#fff', border: 0 }}
                            onClick={onConfirmCancel}
                            disabled={loadingAction || !cancelReason.trim()}
                        >
                            {loadingAction ? 'Cancelando…' : 'Confirmar cancelamento'}
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Motivo do cancelamento" required>
                    <KitTextarea
                        value={cancelReason}
                        onChange={(e) => onCancelReasonChange(e.target.value)}
                        placeholder="Descreva o motivo…"
                    />
                </KitField>
            </MobileSheet>
        </Box>
    );
}

